import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, Circle, ExternalLink, PhoneCall, Info, HelpCircle, AlertTriangle, BookMarked } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import PartnerCard from "@/components/PartnerCard";
import PaperLogsEducation from "@/components/paperlog/PaperLogsEducation";
import LlcExplainer from "@/components/LlcExplainer";
import EinExplainer from "@/components/EinExplainer";
import AuthorityExplainer from "@/components/AuthorityExplainer";
import Boc3InsuranceExplainer from "@/components/Boc3InsuranceExplainer";
import UcrClearinghouseExplainer from "@/components/UcrClearinghouseExplainer";
import EldIftaExplainer from "@/components/EldIftaExplainer";
import DqFileExplainer from "@/components/DqFileExplainer";
import DrugConsortiumExplainer from "@/components/DrugConsortiumExplainer";
import SafetyAuditExplainer from "@/components/SafetyAuditExplainer";
import FactoringExplainer from "@/components/FactoringExplainer";
import LaneOptimizationExplainer from "@/components/LaneOptimizationExplainer";
import DispatchScalingExplainer from "@/components/DispatchScalingExplainer";
import { getStep, PLAYBOOK_STEPS } from "@/data/playbook";
import { getPartnersForStep, PARTNER_DISCLOSURE } from "@/data/partners";
import { useProgress } from "@/hooks/useProgress";
import { useAuth } from "@/context/AuthContext";

// Per-step plain-English explainers (rendered under the What/Why cards).
const STEP_EXPLAINERS = {
  1: LlcExplainer, 2: EinExplainer, 3: AuthorityExplainer, 4: Boc3InsuranceExplainer,
  5: UcrClearinghouseExplainer, 6: EldIftaExplainer, 7: DqFileExplainer, 8: DrugConsortiumExplainer,
  9: SafetyAuditExplainer, 10: FactoringExplainer, 11: LaneOptimizationExplainer, 12: DispatchScalingExplainer,
};

export default function StepDetail() {
  const { n } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDone, toggle } = useProgress();
  const { user } = useAuth();
  const step = getStep(n);

  useEffect(() => {
    if (location.hash === "#pitfall") {
      const t = setTimeout(() => {
        const el = document.getElementById("pitfall");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [location, n]);

  if (!step) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-3xl font-black text-slate-900">Step not found</h1>
          <Link to="/" className="text-brand font-bold mt-4 inline-block">Back to the Guide</Link>
        </div>
      </div>
    );
  }

  const prev = getStep(step.n - 1);
  const next = getStep(step.n + 1);
  const done = isDone(step.n);
  const stepPartners = getPartnersForStep(step.n);
  const StepExplainer = STEP_EXPLAINERS[step.n];

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Step header */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <Link to="/" data-testid="back-to-playbook" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> All 12 steps
          </Link>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-5xl font-bold tabular text-brand">{String(step.n).padStart(2, "0")}</span>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 border border-white/15 rounded-full px-3 py-1">
                {step.tag}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black leading-[1]">
              {step.title}
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">{step.overview}</p>
            <button
              onClick={() => toggle(step.n)}
              data-testid="toggle-complete-button"
              className={`mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-bold transition-colors ${
                done ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white text-slate-900 hover:bg-slate-100"
              }`}
            >
              {done ? <><CheckCircle2 className="w-5 h-5" /> Step completed</> : <><Circle className="w-5 h-5" /> Mark step complete</>}
            </button>
            <div>
              <Link to="/glossary" data-testid="step-glossary-link" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-brand transition-colors">
                <BookMarked className="w-4 h-4" /> Confused by a term? Open the Key Terms glossary
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What it is / Why it matters */}
      <section className="max-w-4xl mx-auto px-6 pt-16">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-md p-6 bg-slate-50" data-testid="what-it-is">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-brand" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">What it is</span>
            </div>
            <p className="text-slate-700 leading-relaxed">{step.whatItIs}</p>
          </div>
          <div className="border border-slate-200 rounded-md p-6 bg-slate-50" data-testid="why-it-matters">
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="w-4 h-4 text-brand" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Why it matters</span>
            </div>
            <p className="text-slate-700 leading-relaxed">{step.why}</p>
          </div>
        </div>

        {step.note && (
          <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 rounded-md p-6 scroll-mt-24" id="pitfall" data-testid="step-note">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-amber-700">Common pitfall — and how it happens</span>
            </div>
            <p className="text-amber-900 leading-relaxed">{step.note}</p>
          </div>
        )}
      </section>

      {StepExplainer && (
        <section className="max-w-4xl mx-auto px-6 pt-8" data-testid="step-explainer">
          <StepExplainer />
        </section>
      )}

      {/* Body */}
      <section className="max-w-4xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-12">
        {/* Tasks */}
        <div className="lg:col-span-3">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-6">What you'll do</div>
          <ul className="space-y-4">
            {step.tasks.map((t, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                className="flex gap-3 text-slate-800"
              >
                <CheckCircle2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                <span className="leading-relaxed">{t}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        <div className="lg:col-span-2">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-6">Official resources</div>
          <div className="space-y-3">
            {step.resources.map((r) => (
              <a
                key={r.url}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`resource-link-${step.n}`}
                className="group block border border-slate-200 rounded-md p-4 hover:border-brand transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="font-semibold text-slate-900 text-sm leading-snug">{r.label}</span>
                  <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-brand shrink-0 transition-colors" />
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{r.desc}</p>
              </a>
            ))}
          </div>

          {stepPartners.length > 0 && (
            <div className="mt-8" data-testid="partner-tools">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Recommended tools</div>
              <p className="text-[11px] text-slate-400 leading-relaxed mb-4">{PARTNER_DISCLOSURE}</p>
              {stepPartners.map((cat) => (
                <div key={cat.id} className="mb-5">
                  <div className="text-[11px] font-bold text-slate-600 mb-2">{cat.label}</div>
                  <div className="space-y-3">
                    {cat.items.map((item) => (
                      <PartnerCard key={item.id} item={item} category={cat.id} stepN={step.n} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {step.n === 6 && (
        <section className="max-w-4xl mx-auto px-6 pb-4">
          <PaperLogsEducation practiceTo="/paper-logs" isPaid={!!user?.subscription_active} />
        </section>
      )}

      {/* Prev / Next */}
      <section className="border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between gap-4">
          {prev ? (
            <Link to={`/step/${prev.n}`} data-testid="prev-step" className="group flex items-center gap-3 text-left">
              <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-brand transition-colors" />
              <span>
                <span className="block text-xs text-slate-400">Previous</span>
                <span className="block font-semibold text-slate-900 text-sm">{prev.title}</span>
              </span>
            </Link>
          ) : <span />}
          {next ? (
            <Link to={`/step/${next.n}`} data-testid="next-step" className="group flex items-center gap-3 text-right">
              <span>
                <span className="block text-xs text-slate-400">Next</span>
                <span className="block font-semibold text-slate-900 text-sm">{next.title}</span>
              </span>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-brand transition-colors" />
            </Link>
          ) : (
            <Button onClick={() => navigate("/diagnostic")} data-testid="finish-to-diagnostic" className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold h-12 px-6">
              Check your readiness <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </section>

      {/* Delegate CTA */}
      {step.n >= 5 ? (
        <section className="bg-brand text-white border-t border-brand" data-testid="paperwork-relief-cta">
          <div className="max-w-4xl mx-auto px-6 py-14 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl font-black tracking-tight">Paperwork piling up?</h3>
              <p className="text-orange-50 mt-2 max-w-xl leading-relaxed">
                Between authority, insurance, UCR, Clearinghouse, ELD, DQ files and audits, the filings add up fast — and one missed detail can cost you. If it's too much, you don't have to grind through it alone. Let RouteHaul handle the paperwork so you can focus on driving.
              </p>
            </div>
            <Link to="/diagnostic" data-testid="delegate-cta" className="shrink-0">
              <Button className="rounded-full bg-white text-brand hover:bg-orange-50 h-12 px-6 font-bold">
                <PhoneCall className="w-4 h-4 mr-2" /> Let us handle it
              </Button>
            </Link>
          </div>
        </section>
      ) : (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-4xl mx-auto px-6 py-14 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-xl font-semibold text-slate-900">Want this handled for you?</h3>
              <p className="text-slate-600 mt-1">RouteHaul Managed Services can file, comply and dispatch on your behalf.</p>
            </div>
            <Link to="/diagnostic" data-testid="delegate-cta" className="shrink-0">
              <Button variant="outline" className="rounded-full border-slate-300 h-12 px-6 font-bold">
                <PhoneCall className="w-4 h-4 mr-2" /> Talk to us
              </Button>
            </Link>
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between">
          <span>Step {step.n} of {PLAYBOOK_STEPS.length}</span>
          <span className="font-mono">RouteHaul Logistics Pathways</span>
        </div>
      </footer>
    </div>
  );
}
